import {
  LOCAL_WORKER_REGISTRY,
  LOCAL_WORKER_STORAGE_KEY,
  getLocalWorkerUrl,
} from './local-worker-registry.js';
import { sitePolicyFromHost, SFW_ORIGIN, FULL_ORIGIN, SFW_DEV_PORT } from './shared/site-policy.js';
import { CRYPTO_PACKS } from './shared/crypto-packs.js';

export { SFW_ORIGIN, FULL_ORIGIN };

const isLocalHost = () => {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
};

/**
 * On localhost only: workers local (default) or all remote.
 * Local workers allow localhost:8001 and :8002. Deployed workers do not.
 * Local:  localStorage.setItem('kissandra_use_local_workers','1'); location.reload()
 * Remote: localStorage.setItem('kissandra_use_local_workers','0'); location.reload()
 */
const useLocalWorkers = () => {
  if (!isLocalHost()) return false;
  try {
    const value = localStorage.getItem(LOCAL_WORKER_STORAGE_KEY);
    if (value === null) return true;
    return value === '1' || value === 'true';
  } catch {
    return true;
  }
};

const resolveWorkerUrl = (folderName) => {
  const entry = LOCAL_WORKER_REGISTRY[folderName];
  if (!entry) throw new Error(`Unknown worker registry key: ${folderName}`);
  if (useLocalWorkers()) {
    const localUrl = getLocalWorkerUrl(folderName);
    if (localUrl) return localUrl;
  }
  return entry.remoteUrl;
};

export const SERVER_URL = resolveWorkerUrl('kssdra-backend');
export const OBS_WORKER_URL = resolveWorkerUrl('observability');
export const USER_WORKER_URL = resolveWorkerUrl('user-worker');
export const WORKER_URL = resolveWorkerUrl('data-fetching');
export const CREATE_WORKER_URL = resolveWorkerUrl('create-worker');
export const CREATE_SPRITE_WORKER_URL = resolveWorkerUrl('create-sprite-worker');
export const GROK_WORKER_URL = resolveWorkerUrl('hangout');
export const GENERATION_API_URL = resolveWorkerUrl('replicate-image-generator');
export const DELAY_ESTIMATION_URL = resolveWorkerUrl('image-producer');
export const GOOGLE_CLIENT_ID = '1074507845286-oiikv1tslnem98d3tq61fph2l0tsquks.apps.googleusercontent.com';
export const STRIPE_WORKER_URL = resolveWorkerUrl('stripe-customer');
export const BILLING_NSFW_WORKER_URL = resolveWorkerUrl('billing-nsfw');
const isSfwDevPage = () => {
  if (typeof window === 'undefined') return false;
  const { hostname, port } = window.location;
  return (hostname === 'localhost' || hostname === '127.0.0.1') && port === SFW_DEV_PORT;
};

export const SITE_POLICY = (() => {
  if (typeof window === 'undefined') return 'full';
  if (isSfwDevPage()) return 'sfw';
  if (window.SITE_POLICY === 'sfw' || window.SITE_POLICY === 'full') return window.SITE_POLICY;
  return sitePolicyFromHost(window.location.hostname);
})();
export const isSfwSite = () => SITE_POLICY === 'sfw';
export const catalogJsonUrl = (relativePath) => {
  const path = String(relativePath || '').replace(/^\//, '');
  const useSfwSibling = isSfwSite()
    && /\.json$/.test(path)
    && !path.endsWith('.sfw.json')
    && /(?:characters_rankings(?:_modal)?\.json$|scenarios-[a-z0-9-]+\.json$|outfits-[a-z0-9-]+\.json$|loc\/[a-z0-9-]+\.json$)/.test(path);
  const file = useSfwSibling ? path.replace(/\.json$/, '.sfw.json') : path;
  return `${CDN_URL}/${file}`;
};
export const STRIPE_PORTAL_LOGIN_FALLBACK = 'https://billing.stripe.com/p/login/5kQcN67VO9bfcz82Tg97G00';
export const STT_WORKER_URL = resolveWorkerUrl('stt');
// Smart CDN URL: uses current site domain (kissandra.ai) or CDN in local dev
export const CDN_URL = (() => {
  if (typeof window === 'undefined') return 'https://kissandra.ai/cdn';
  const { hostname, origin } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
  if (isLocal && isSfwSite()) return `${SFW_ORIGIN}/cdn`;
  if (isLocal) return 'https://cdn.kissandra.ai';
  return `${origin}/cdn`;
})();

// ========================================
// STRIPE CONFIGURATION
// ========================================
// 
// TO SWITCH BETWEEN TEST AND LIVE MODE:
// 2. Update PAYMENT_LINKS below
// 3. Update your webhook worker's environment variables
//
// TEST MODE:
// - PAYMENT_LINKS: Use test links
// 
// LIVE MODE:
// - PAYMENT_LINKS: Use live links
// ========================================

// Stripe Configuration
// Replace these keys manually when switching between test and live mode
const STRIPE_CONFIG = {
  // Payment Links for each plan (Stripe handles checkout session creation automatically)
  PAYMENT_LINKS: {
    free: null,
    // Preferred key for the paid 48h Starter trial offer.
    starter_paid_trial: 'https://buy.stripe.com/3cIbJ2ekc0EJdDcctQ97G0f',
    // Backward-compatible alias used across legacy trial modal code.
    trial: 'https://buy.stripe.com/3cIbJ2ekc0EJdDcctQ97G0f',
    starter_monthly: 'https://buy.stripe.com/28EdRa4JC3QV0Qq2Tg97G09',
    starter_yearly: 'https://buy.stripe.com/fZu8wQcc4fzDcz81Pc97G0b',
    single_monthly: 'https://buy.stripe.com/8x28wQdg8bjn6aKbpM97G07',
    single_yearly: 'https://buy.stripe.com/4gMdRa4JCevz1UuctQ97G0d',
    harem_monthly: 'https://buy.stripe.com/fZu6oI6RK1INbv43Xk97G05',  
    harem_yearly: 'https://buy.stripe.com/fZucN6gsk87bar0ctQ97G0c'
  },

};

window.STRIPE_CONFIG = isSfwSite() ? STRIPE_CONFIG : { PAYMENT_LINKS: {} };
window.SITE_POLICY = SITE_POLICY;
window.CRYPTO_PLANS = isSfwSite() ? null : CRYPTO_PACKS;

if (typeof window !== 'undefined') {
  try {
    if (!window.CONFIG) {
      window.CONFIG = {
        SERVER_URL,
        USER_WORKER_URL,
        WORKER_URL,
        CREATE_WORKER_URL,
        CREATE_SPRITE_WORKER_URL,
        GROK_WORKER_URL,
        GENERATION_API_URL,
        GOOGLE_CLIENT_ID,
        CDN_URL,
      };
    }
  } catch (e) {
    console.error('Failed to set global config:', e);
  }
}
